import { ReactNode } from 'react'
import {
  ChevronDown,
  ChevronUp,
  Download,
  EllipsisVertical,
} from 'lucide-react'

import { Button } from '@/view/components/ui/button'

import {
  SUPPORT_STATUS_LABEL,
  SupportRequestAttachment,
  SupportRequestReason,
  SupportRequestStatusEnum,
} from '@/app/entities/SupportRequest'
import { cn } from '@/app/utils/cn'
import { handleDownload } from '@/app/utils/download'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/view/components/ui/dropdown-menu'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/view/components/ui/sheet'
import { Spinner } from '@/view/components/ui/spinner'

import { CommentBox } from './CommentBox'
import { SupportRequestHistorySection } from './SupportRequestHistory'
import {
  DetailField,
  formatFileSize,
  useSupportRequestDetailSheet,
} from './useSupportRequestDetailSheet'

interface ItemProps {
  label: string
  content: ReactNode
  type?: 'default' | 'row'
}

const Item = ({ label, content, type = 'default' }: ItemProps) => {
  return (
    <div
      className={cn(
        'flex items-center gap-2',
        type === 'row' && 'flex-col items-start gap-1',
      )}
    >
      <p className="whitespace-nowrap text-sm font-semibold text-black04">
        {label}
      </p>
      {type === 'default' && (
        <div className="h-1 w-full border-b-[1px] border-dotted border-[#CACACA] " />
      )}
      <div
        className={cn(
          'text-xs font-semibold',
          type === 'default' ? 'max-w-96 whitespace-nowrap' : 'w-full',
        )}
      >
        {content}
      </div>
    </div>
  )
}

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <p className="text-[11px] font-semibold uppercase tracking-wide text-black04">
    {children}
  </p>
)

interface SupportRequestDetailsSheetProps {
  visible: boolean
  number: string | null
  onClose: () => void
}

export const SupportRequestDetailsSheet = ({
  visible,
  number,
  onClose,
}: SupportRequestDetailsSheetProps) => {
  const {
    user,
    supportRequest,
    isLoading,
    infoFields,
    textFields,
    history,
    isAttachmentsOpen,
    setIsAttachmentsOpen,
    allowAttachments,
    canSubmitMessage,
    message,
    setMessage,
    files,
    fileInputRef,
    handleSelectFiles,
    handleRemoveFile,
    handleOpenFilePicker,
    sendMessage,
    isSending,
    canComment,
    hasMessage,
    isSupportStaff,
    supportOptions,
    ownerOptions,
    updateStatus,
    isUpdatingStatus,
    sellerFields,
  } = useSupportRequestDetailSheet({ visible, number })

  return (
    <Sheet open={visible} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-[340px] overflow-y-auto rounded-2xl p-2 xs:mr-5 xs:mt-5 xs:h-[95%] xs:w-96 xs:p-5 sm:max-w-none md:w-[440px]">
        <div className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle className="text-xl font-semibold">
              <div className="flex items-center justify-between gap-2">
                <span>Detalhes do chamado</span>

                {!isSupportStaff && !!ownerOptions.length && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        disabled={isUpdatingStatus}
                        className="-mr-1 rounded p-1 text-black03 hover:bg-gray-100"
                      >
                        <EllipsisVertical className="h-5 w-5" />
                      </button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end" className="min-w-[180px]">
                      {ownerOptions.map((to) => (
                        <DropdownMenuItem
                          key={to}
                          disabled={isUpdatingStatus}
                          onSelect={() => updateStatus(to)}
                        >
                          {to === SupportRequestStatusEnum.CLOSED
                            ? 'Encerrar chamado'
                            : 'Marcar como resolvido'}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              <div className="my-5 w-full border-b-[1px] border-gray01" />
            </SheetTitle>
          </SheetHeader>

          {isLoading && (
            <div className="flex h-screen items-center justify-center">
              <Spinner />
            </div>
          )}

          {!isLoading && supportRequest && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-3">
                {infoFields.map((field: DetailField) => (
                  <Item key={field.label} {...field} />
                ))}

                {textFields.map((field: DetailField) => (
                  <Item key={field.label} {...field} />
                ))}
              </div>

              {supportRequest.reason !== SupportRequestReason.WHATSAPP_DIRECT && (
                <div className="flex flex-col gap-3">
                  <SectionTitle>Origem</SectionTitle>

                  {sellerFields(supportRequest).map((field: DetailField) => (
                    <Item key={field.label} {...field} />
                  ))}
                </div>
              )}

              {!!supportRequest.resolution && (
                <div className="flex flex-col gap-1 rounded-lg border border-gray-200 p-3">
                  <SectionTitle>Resolução</SectionTitle>
                  <p className="whitespace-break-spaces text-xs font-semibold">
                    {supportRequest.resolution}
                  </p>
                </div>
              )}

              {!!supportRequest.attachments?.length && (
                <div className="rounded-lg border border-gray-200 p-3">
                  <button
                    type="button"
                    onClick={() => setIsAttachmentsOpen((prev) => !prev)}
                    className="flex w-full items-center justify-between"
                  >
                    <SectionTitle>
                      Anexos ({supportRequest.attachments.length})
                    </SectionTitle>
                    {isAttachmentsOpen ? (
                      <ChevronUp className="h-5 w-5 text-black04" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-black04" />
                    )}
                  </button>

                  {isAttachmentsOpen && (
                    <div className="mt-3 flex flex-col gap-2">
                      {supportRequest.attachments.map(
                        (attachment: SupportRequestAttachment) => (
                          <button
                            key={attachment.url}
                            type="button"
                            onClick={() => {
                              window.open(attachment.url, '_blank', 'noopener,noreferrer');
                              handleDownload({
                                url: attachment.url,
                                name: attachment.name,
                              })
                            }}
                            className="flex items-center gap-3 rounded-lg border border-gray-300 bg-transparent p-2 text-left"
                          >
                            {attachment.type === 'image' ? (
                              <img
                                src={attachment.url}
                                alt={attachment.name}
                                className="h-14 w-14 rounded-md border object-cover"
                              />
                            ) : (
                              <video
                                src={attachment.url}
                                className="h-14 w-24 rounded-md border object-cover"
                              />
                            )}
                            <div className="flex min-w-0 flex-1 flex-col">
                              <span className="truncate text-sm text-gray-700">
                                {attachment.name}
                              </span>
                              <span className="text-xs text-black04">
                                {formatFileSize(attachment.size)}
                              </span>
                            </div>
                            <Download className="h-4 w-4 shrink-0 text-gray-400" />
                          </button>
                        ),
                      )}
                    </div>
                  )}
                </div>
              )}

              {(canComment || !!history.length) && (
                <div className="mb-4 flex flex-col gap-2">
                  <SectionTitle>Histórico</SectionTitle>

                  {canComment && (
                    <>
                      <CommentBox
                        message={message}
                        setMessage={setMessage}
                        files={files}
                        fileInputRef={fileInputRef}
                        onSelectFiles={handleSelectFiles}
                        onRemoveFile={handleRemoveFile}
                        onOpenFilePicker={handleOpenFilePicker}
                        onSend={() => sendMessage()}
                        allowAttachments={allowAttachments}
                        canSubmit={canSubmitMessage}
                        isSending={isSending}
                        showSendButton={!isSupportStaff}
                      />

                      {isSupportStaff && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              className="w-fit gap-1 text-white"
                              disabled={isUpdatingStatus || isSending}
                            >
                              Atualizar
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>

                          <DropdownMenuContent
                            align="start"
                            className="min-w-[180px]"
                          >
                            {supportOptions.map((to) => (
                              <DropdownMenuItem
                                key={to}
                                disabled={isUpdatingStatus}
                                onSelect={() => updateStatus(to)}
                                className={cn(
                                  to === SupportRequestStatusEnum.CANCELLED &&
                                  'text-error focus:text-error',
                                )}
                              >
                                {SUPPORT_STATUS_LABEL[to]}
                              </DropdownMenuItem>
                            ))}

                            {!!supportOptions.length && (
                              <DropdownMenuSeparator />
                            )}

                            <DropdownMenuItem
                              disabled={!hasMessage || isSending}
                              onSelect={() => sendMessage()}
                            >
                              Salvar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </>
                  )}

                  <SupportRequestHistorySection
                    history={history}
                    currentUserId={user?._id}
                    isSupportStaff={isSupportStaff}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
